import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  Actions,
  ATTRIBUTES_DEPENDENT_ON_JOURNEY_TOKEN_SET,
  ATTRIBUTES_WITH_RHS_DEPENDENT_DROPDOWNS_SET,
} from './constants';

import Logician, { AND_OPERATOR, OR_OPERATOR } from './Logician';

// return type: string | number | null
const getValueDependentOnByPlaceholder = ({
  attrComponents,
  placeholder: _placeholder,
}) => {
  const attrComponent = attrComponents.find(({ placeholder }) => placeholder === _placeholder);
  return attrComponent.value || attrComponent.default || null;
};

// return type: string | number | null
const getValueDependentOn = ({
  attrComponents,
  attrName,
}) => {
  if (ATTRIBUTES_DEPENDENT_ON_JOURNEY_TOKEN_SET.has(attrName)) {
    const journeyToken = getValueDependentOnByPlaceholder({
      attrComponents,
      placeholder: '{journey}',
    });
    return journeyToken;
  }

  if (attrName === 'alh_latest_eval_outcome') {
    const workflowToken = getValueDependentOnByPlaceholder({
      attrComponents,
      placeholder: '{workflow}',
    });
    return workflowToken;
  }

  return null;
};

const getComponentDisplayValue = ({ defaultValue, possibleValues, value: _value }) => {
  const value = _value === null || _value === undefined
    ? defaultValue
    : _value;
  if (Array.isArray(possibleValues)) {
    const [, label] = possibleValues.find(([val]) => val === value) || [];
    return label || value;
  }
  return value;
};


const formatMultilevelAttribute = (name, components, attributeName) => {
  if (!components.length) return '';
  const componentsByPlaceholder = components.reduce((acc, component) => {
    acc[component.placeholder] = component;
    return acc;
  }, {});

  // the leading space is necessary in the regex pattern because with it, it can remove optional
  // components that are null or undefined. Without the leading space, an extra space is left where
  // the removed component was.
  return name.replace(/ {\w*}/g, match => {
    const placeholder = match.slice(1); // get rid of leading space
    const component = componentsByPlaceholder[placeholder];
    if ((component?.is_optional && (component.value === null || component.value === undefined))
      || !component) {
      return '';
    }

    const displayValue = getComponentDisplayValue({
      defaultValue: component.default,
      possibleValues: component.possible_values,
      value: component.value,
    });

    if (['th_multilevel', 'th_nonmonetary_multilevel'].includes(attributeName) && placeholder !== '{formula}') {
      const label = placeholder.replaceAll(/[{}]/g, '');
      return ` ${label}: ${displayValue};`;
    }

    return ` ${displayValue}`;
  });
};


const BooleanRuleRendererPropTypes = {
  tags: PropTypes.array.isRequired,
  actions: PropTypes.array.isRequired,
  services: PropTypes.object.isRequired,
  reasonCodeGroups: PropTypes.array.isRequired,
  thresholdsRule: PropTypes.string.isRequired,
  actionThresholds: PropTypes.array.isRequired,
};

class BooleanRuleRenderer extends React.Component {
  static getDependentDropdownOptions({
    attrComponents,
    attrName,
    rightSideDropdownOptions,
  }) {
    const valueDependentOn = getValueDependentOn({
      attrComponents,
      attrName,
    });

    if (valueDependentOn) {
      return rightSideDropdownOptions[valueDependentOn] || [];
    }

    return [];
  }

  static getRightAttrDisplayValue({ attr, attrComponents, rightAttrValue }) {
    if (attr.sub_data_type === 'dropdown') {
      const isDependentDropdown = ATTRIBUTES_WITH_RHS_DEPENDENT_DROPDOWNS_SET
        .has(attr.attribute_name);

      const options = isDependentDropdown
        ? BooleanRuleRenderer.getDependentDropdownOptions({
          attrComponents,
          attrName: attr.attribute_name,
          rightSideDropdownOptions: attr.right_side_dropdown_options,
        })
        : attr.right_side_dropdown_options;

      const rightAttrDisplayValue = ((options || [])
        .find(({ value }) => value === rightAttrValue))?.label;

      return rightAttrDisplayValue;
    }

    return null;
  }

  static getStaticThresholdValueForNullOperators(leftSide, threshold) {
    const {
      rightAttrValue,
      operator,
    } = threshold;
    if (
      rightAttrValue === null
      && ['e', 'ne'].includes(operator)
    ) {
      if (threshold.operator === 'ne') {
        return `${leftSide} is not null`;
      }
      // For 'e' or default equal operator
      return `${leftSide} is null`;
    }

    return null;
  }

  static getThresholdString(props, threshold) {
    // Requires services, actions, reasonCodeGroups, tags in props object
    const { services } = props;

    if (!threshold) return '';
    const leftAction = props.actions.find(a => a.id === threshold.leftActionId);
    // Action may not be defined, if optional filter is not applied
    if (leftAction === undefined) return '';

    if (leftAction.type === 'run') {
      // Threshold is on a run-type Action
      const leftService = props.services[leftAction.serviceId];
      const leftAttr = services[leftAction.serviceId].attributes[threshold.leftAttrId];
      if (threshold.leftAttrId !== null && leftAttr) {
        // Threshold's left side is using a Service Attribute
        const leftAttrType = leftAttr?.dataType;
        const leftAttrName = leftAttr?.isMultilevel
          ? formatMultilevelAttribute(
            leftAttr.description,
            threshold.leftAttrComponents,
            leftAttr.attribute_name,
          ) : leftAttr.name;
        const leftSide = `run:${leftService.name}:${leftAttrName}`;

        // Threshold's left Service Attribute is being compared to static value
        const staticThresholdValueForNullOperators = BooleanRuleRenderer
          .getStaticThresholdValueForNullOperators(leftSide, threshold);
        if (staticThresholdValueForNullOperators) return staticThresholdValueForNullOperators;
        if (threshold.rightAttrValue !== null) {
          const rightAttrDisplayValue = BooleanRuleRenderer.getRightAttrDisplayValue({
            attr: leftAttr,
            attrComponents: threshold.leftAttrComponents,
            rightAttrValue: threshold.rightAttrValue,
          });

          return BooleanRuleRenderer.formatStaticThreshold(
            leftSide,
            leftAttrType,
            threshold,
            rightAttrDisplayValue,
          );
        }

        if (leftAttrType === 'reasoncodes' && threshold.rightCRGId !== null) {
          // If threshold is on a reason code group, get the group name
          const operator = BooleanRuleRenderer.formatOperator(threshold.operator);
          const group = props.reasonCodeGroups.find(g => g.id === threshold.rightCRGId);
          const reasonCodeGroupString = `${group.name}${group.archived ? ' (Deleted)' : ''}`;
          return `${leftSide} ${operator} ${reasonCodeGroupString}`;
        }

        if (threshold.rightActionId !== null) {
          return BooleanRuleRenderer.formatMatchingThreshold(leftSide, threshold, props);
        }
      } else if (threshold.leftCategoryId !== null && threshold.rightActionId !== null) {
        // Matching a left action category. Get the left action service and category
        // and the right service name
        const leftCategory = leftService.categories.response
          .find(category => category.id === threshold.leftCategoryId);
        const leftSide = `${leftAction.type}:${leftService.name} response data ${leftCategory.name}`;
        return BooleanRuleRenderer.formatMatchingThreshold(leftSide, threshold, props);
      }
    } else if (['input', 'output', Actions.MATRIX_MODEL, 'imported_output_attribute', 'imported_matrix_model'].includes(leftAction.type)) {
      let associatedPublishedAttribute;
      if (leftAction.publishedAttributeToken) {
        associatedPublishedAttribute = props
          .publishedAttributes?.find(({ published_attribute_token: paToken }) => (
            leftAction?.publishedAttributeToken === paToken
          ));
      }
      const thresholdStr = `${leftAction.type}:${associatedPublishedAttribute
        ? associatedPublishedAttribute.published_attribute_name : leftAction.name}`;
      const staticThresholdValueForNullOperators = BooleanRuleRenderer
        .getStaticThresholdValueForNullOperators(thresholdStr, threshold);
      if (staticThresholdValueForNullOperators) return staticThresholdValueForNullOperators;
      if (threshold.rightAttrValue !== null) {
        let leftActionDataType = 'decimal';
        if (leftAction.type === 'input') {
          if (associatedPublishedAttribute) {
            leftActionDataType = associatedPublishedAttribute?.data_type;
          } else {
            leftActionDataType = props.actionOptions
              ? props.actionOptions
                .find(ao => ao.actionId === leftAction.id && ao.key === 'data_type').value
              : leftAction.options.data_type.value;
          }
        }
        return BooleanRuleRenderer.formatStaticThreshold(
          thresholdStr,
          leftActionDataType,
          threshold,
        );
      }

      if (threshold.rightActionId !== null) {
        return BooleanRuleRenderer.formatMatchingThreshold(thresholdStr, threshold, props);
      }
    } else if (leftAction.type === 'outcome_reason') {
      const textIsPassedIn = threshold.rightAttrValue === 'true' ? 'set' : 'not set';
      return `imported outcome reason:${leftAction.name} is ${textIsPassedIn}`;
    } else {
      // Threshold is on a tag-type Action
      const tagName = props.tags
        .find(tag => tag.customer_tag_id === leftAction.customerTagId)?.tag_name;

      if (tagName) {
        const tagSet = threshold.rightTagActionSet ? 'set' : 'not set';
        return `tag:${tagName} is ${tagSet}`;
      }

      return '';
    }
    // I feel like this should break something, but it doesn't seem to
    // this is just here appease the linter
    return undefined;
  }


  static formatOperator(operator) {
    switch (operator) {
      case 'lt':
        return 'is less than';
      case 'le':
        return 'is less than or equal to';
      case 'gt':
        return 'is greater than';
      case 'ge':
        return 'is greater than or equal to';
      case 'ne':
        return 'is not equal to';
      case 'range':
        return 'is in the range of';
      case 'in':
        return '(at least one) are contained in';
      case 'nin':
        return 'are not contained in';
      case 'con':
        return 'contains';
      case 'ncon':
        return 'does not contain';
      default:
        return 'is equal to';
    }
  }


  static formatDateOperator(operator) {
    switch (operator) {
      case 'lt':
        return 'is before';
      case 'lte':
        return 'is on or before';
      case 'gt':
        return 'is after';
      case 'gte':
        return 'is on or after';
      case 'ne':
        return 'is not on';
      case 'range':
        return 'is in the range of';
      default:
        return 'is on';
    }
  }


  static formatStaticThreshold(thresholdString, attrType, threshold, rightAttrDisplayValue) {
    let operator = BooleanRuleRenderer.formatOperator(threshold.operator);
    let { rightAttrValue } = threshold;

    // Format the threshold into english depending upon threshold value type
    if (attrType === 'decimal' || attrType === 'integer') {
      // Threshold value type is a number
      if (threshold.operator === 'range') {
        // Threshold operator is range
        const relValues = rightAttrValue.split(',');
        rightAttrValue = `${relValues[0]} to ${relValues[1]}`;
      }
    } else if (attrType === 'boolean') {
      // Threshold value type is a boolean
      rightAttrValue = threshold.rightAttrValue === 'true' ? 'True' : 'False';
    } else if (attrType === 'string') {
      // Threshold value type is a string
      rightAttrValue = rightAttrValue.replace(',', ' or ');
      if (threshold.operator === 'in') operator = 'matches (regex)';
      if (threshold.operator === 'nin') operator = 'does not match (regex)';
    } else if (attrType === 'date') {
      rightAttrValue = moment(rightAttrValue).format('YYYY-MM-DD');
      operator = BooleanRuleRenderer.formatDateOperator(threshold.operator);
    }

    if (rightAttrDisplayValue) {
      rightAttrValue = rightAttrDisplayValue;
    }

    return `${thresholdString} ${operator} ${rightAttrValue}`;
  }


  static formatMatchingThreshold(thresholdString, threshold, props) {
    // If the threshold is a matching threshold, we need to get the right action info
    const rightAction = props.actions.find(a => a.id === threshold.rightActionId);
    const rightService = props.services[rightAction.serviceId];
    if (threshold.rightAttrId !== null) {
      // If we're matching against an attribute, get the attribute name
      const rightAttr = rightService.attributes[threshold.rightAttrId];
      const rightAttrName = rightAttr.isMultilevel
        ? formatMultilevelAttribute(
          rightAttr.description,
          threshold.rightAttrComponents,
          rightAttr.attribute_name,
        ) : rightAttr.name;
      const operator = BooleanRuleRenderer.formatOperator(threshold.operator);
      return `${thresholdString} ${operator} ${rightAction.type}:${rightService.name}:${rightAttrName}`;
    }

    if (threshold.rightCategoryId !== null) {
      // If we're matching against a category, get the category name
      const rightCategory = rightService.categories.response
        .find(category => category.id === threshold.rightCategoryId);
      return `${thresholdString} matches ${rightAction.type}:${rightService.name} response data ${rightCategory.name}`;
    }

    return `${thresholdString} matches ${rightAction.type}:${rightAction.name}`;
  }


  constructor(props) {
    super(props);

    ['getThresholdString', 'renderThresholdsRule'].forEach(method => {
      this[method] = this[method].bind(this);
    });
  }


  getThresholdString(threshold) {
    const {
      tags,
      actions,
      services,
      reasonCodeGroups,
    } = this.props;
    return BooleanRuleRenderer.getThresholdString(
      {
        tags,
        actions,
        services,
        reasonCodeGroups,
      },
      threshold,
    );
  }


  renderThresholdsRule(thresholdsRule) {
    const thresholdsObj = {};
    thresholdsRule.forEach((value, index) => { thresholdsObj[index] = value; });
    const { actionThresholds } = this.props;

    return (
      <ul className="requirement-thresholds">
        {Object.keys(thresholdsObj).map(index => {
          const value = thresholdsObj[index];
          if (value === AND_OPERATOR) {
            return <li key={`&${index}`} className="bold operator">AND</li>;
          }
          if (value === OR_OPERATOR) return <li key={`|${index}`} className="bold operator">OR</li>;
          if (Array.isArray(value)) {
            return <li key={`group${index}`}>{this.renderThresholdsRule(value)}</li>;
          }

          const threshold = actionThresholds.find(t => t.id === value);
          return <li key={`item${index}`}>{this.getThresholdString(threshold)}</li>;
        })}
      </ul>
    );
  }


  render() {
    const { thresholdsRule: propsThresholdRule } = this.props;
    const thresholdsRule = new Logician(propsThresholdRule).toArray();

    return thresholdsRule.length ? this.renderThresholdsRule(thresholdsRule) : (
      <ul className="requirement-thresholds">
        <li className="dependent">This always happens.</li>
      </ul>
    );
  }
}
BooleanRuleRenderer.propTypes = BooleanRuleRendererPropTypes;

export default BooleanRuleRenderer;
